<template>
  <div>
    <v-overlay v-if="loading" :absolute="true">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div v-if="!loading">
      <!-- Shift Tabs -->
      <v-tabs
        v-model="selectedShift"
        class="mt-4"
        background-color="transparent"
        color="primary"
        slider-color="primary"
      >
        <v-tab v-for="(shift, key) in shiftTabs" :key="key" class="text-capitalize">
          {{ shift.label }}
        </v-tab>
      </v-tabs>

      <!-- Tab Items -->
      <v-tabs-items v-model="selectedShift" class="mt-4">
        <v-tab-item v-for="(shift, shiftIndex) in shiftTabs" :key="shiftIndex">
          <!-- Categories Grid - 4 per row -->
          <v-row v-if="filteredCategorizedFoods.length > 0" dense>
            <v-col
              v-for="category in filteredCategorizedFoods"
              :key="category.id"
              cols="12"
              sm="6"
              md="3"
            >
              <div class="category-section">
                <!-- Category Header -->
                <div class="category-header mb-3">
                  <span class="category-name">{{ toLatin(category.name) }}</span>
                </div>

                <!-- Foods List - 1 per row -->
                <div class="foods-list">
                  <div v-for="food in category.foods" :key="food.id" class="mb-2">
                    <meal-order-card
                      :meal="food"
                      :shift-key="currentShiftKey"
                      @quantity-changed="handleQuantityChange"
                    />
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Empty State -->
          <empty-state
            v-else
            icon="mdi-food-off"
            :title="$t('no_meals_available')"
            :subtitle="
              $t('no_meals_for_shift_and_date', {
                shift: shiftTabs[selectedShift].label.toLowerCase(),
                date: localeDate(selectedDate),
              })
            "
          >
            <div class="text-body-2 grey--text mb-4">
              {{ $t("add_meals_to_menu_first") }}
            </div>
            <primary-button :to="{ name: 'jelovnik' }" icon="mdi-food-variant">
              {{ $t("go_to_menu") }}
            </primary-button>
          </empty-state>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import menuService from "@/services/menu";
import categoryService from "@/services/category";
import hasSnackbar from "@/mixins/hasSnackbar";
import MealOrderCard from "@/components/report/meals/components/MealOrderCard.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import { SHIFTS } from "@/constants/shifts";

export default {
  name: "AdditionalMealsForGroupCompanies",
  components: {
    MealOrderCard,
    EmptyState,
  },
  mixins: [hasSnackbar],
  props: {
    company: {
      type: Object,
      required: true,
    },
    selectedDate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      menus: [],
      categories: [],
      categorizedFoods: [],
      selectedShift: 0,
    };
  },
  computed: {
    shiftTabs() {
      return SHIFTS.map((shift) => ({
        ...shift,
        label: this.$t(shift.label),
      }));
    },
    currentShiftKey() {
      return this.shiftTabs[this.selectedShift]?.key || "shift_one";
    },
    filteredCategorizedFoods() {
      // Filter categories and foods based on current shift availability
      return this.categorizedFoods
        .map((category) => {
          const filteredFoods = category.foods.filter((food) => {
            // Check if food is available for current shift (1 = available, 0 = not available)
            return food[this.currentShiftKey] === 1;
          });
          return {
            ...category,
            foods: filteredFoods,
          };
        })
        .filter((category) => category.foods.length > 0); // Only show categories with available foods
    },
  },
  created() {
    this.loadMenus();
  },
  methods: {
    async loadMenus() {
      this.loading = true;
      try {
        // Step 1: Fetch categories
        const { data: categoriesData } = await categoryService.getCategories();
        this.categories = categoriesData;

        // Step 2: Get menus for selected date
        const { data: menusData } = await menuService.getWebAppOnlyMenu(
          this.selectedDate,
          this.selectedDate
        );

        // Step 3: Fetch detailed menu data with meals for each menu
        const menusWithMeals = [];
        for (const menu of menusData) {
          try {
            const { data: menuDetail } = await menuService.getMenuById(menu.id, true);
            menusWithMeals.push(menuDetail);
          } catch (error) {
            console.error(`Error fetching menu ${menu.id}:`, error);
          }
        }

        this.menus = menusWithMeals;
        this.categorizeFoods(menusWithMeals);
      } catch (error) {
        console.error("Error loading menus:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_menu"));
      } finally {
        this.loading = false;
      }
    },

    categorizeFoods(menus) {
      const foodsByCategory = {};

      // First, collect all foods by category
      menus.forEach((menu) => {
        if (menu.meals && Array.isArray(menu.meals)) {
          menu.meals.forEach((meal) => {
            if (meal.foods && Array.isArray(meal.foods)) {
              meal.foods.forEach((food) => {
                const categoryId = food.category_id;
                if (!foodsByCategory[categoryId]) {
                  foodsByCategory[categoryId] = [];
                }
                // Check if food already exists in category (by id)
                const exists = foodsByCategory[categoryId].find((f) => f.id === food.id);
                if (!exists) {
                  // Preserve shift availability from the meal
                  const foodWithShifts = {
                    ...food,
                    shift_one: meal.shift_one || 0,
                    shift_two: meal.shift_two || 0,
                    shift_three: meal.shift_three || 0,
                    shift_four: meal.shift_four || 0,
                  };
                  foodsByCategory[categoryId].push(foodWithShifts);
                }
              });
            }
          });
        }
      });

      // Convert to array with category details
      const categoriesWithFoods = [];
      Object.keys(foodsByCategory).forEach((categoryId) => {
        const category = this.categories.find((c) => c.id === parseInt(categoryId));
        if (category && foodsByCategory[categoryId].length > 0) {
          categoriesWithFoods.push({
            ...category,
            foods: foodsByCategory[categoryId],
          });
        }
      });

      // Sort by poredak (order)
      categoriesWithFoods.sort((a, b) => a.poredak - b.poredak);

      this.categorizedFoods = categoriesWithFoods;
      this.initializeFoodOrders();
    },

    initializeFoodOrders() {
      this.categorizedFoods.forEach((category) => {
        category.foods.forEach((food) => {
          if (!food.orders) {
            const orders = {};
            SHIFTS.forEach((shift) => {
              orders[shift.key] = 0;
            });
            this.$set(food, "orders", orders);
          }
        });
      });
    },

    handleQuantityChange({ meal, shiftKey, quantity }) {
      this.$set(meal.orders, shiftKey, quantity);
    },
  },
  watch: {
    selectedDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadMenus();
      }
    },
  },
};
</script>

<style scoped>
.v-tabs-bar {
  background-color: transparent !important;
}

.v-tab {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

.v-tab.v-tab--active {
  background-color: rgba(25, 118, 210, 0.1);
  border-radius: 8px 8px 0 0;
}

.category-section {
  height: 100%;
}

.category-header {
  padding: 10px 14px;
  background-color: rgba(251, 109, 59, 0.2);
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
}

.category-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.foods-list {
  display: flex;
  flex-direction: column;
}
</style>
